import React, { ChangeEvent } from "react";
import { Alert } from "@mui/material";
import { useTranslation } from "react-i18next";
import Provider from "../../../enum/Provider.enum";
import AgentLegalForm from "../../../enum/AgentLegalForm.enum";
import { AgentFormState } from "../../../types/agentTypes";
import { SelectMotivationType } from "../types";
import { Section } from "../styles";

import BaseDataSection from "./BaseDataSection";
import ContractSection from "./ContractSection";
import MotivationSection from "./MotivationSection";
import NameSection from "./NameSection";
import BelarusCitizensSection from "./BelarusCitizensSection";
import PassportSection from "./PassportSection";
import EntrepreneurSection from "./EntrepreneurSection";
import CompanySection from "./CompanySection";
import DocumentsSection from "./DocumentsSection";
import ContactPersonSection from "./ContactPersonSection";
import AddressesSection from "./AddressesSection";
import ContactsSection from "./ContactsSection";
import CloseAgentSection from "./CloseAgentSection";
import FormActions from "./FormActions";

type DefaultTabContentProps = {
  agent: any;
  draft?: any;
  formData: AgentFormState;
  setFormData: React.Dispatch<React.SetStateAction<AgentFormState>>;
  isOgrnipOverflow: boolean;
  setIsOgrnipOverflow: React.Dispatch<React.SetStateAction<boolean>>;
  isCreateMode: boolean;
  isDraftMode: boolean;
  isTechnical: boolean;
  setIsTechnical: React.Dispatch<React.SetStateAction<boolean>>;
  techOnly: boolean;
  provider: Provider;
  motivations: SelectMotivationType[];
  motivationsLoading: boolean;
  loadingTn: boolean;
  tnError: boolean;
  tnClient?: Record<string, any>;
  isFirstNameAmInvalid: boolean;
  isLastNameAmInvalid: boolean;
  isPatronymicAmInvalid: boolean;
  isFormValid: boolean;
  isDirty: boolean;
  isSavingDraft: boolean;
  isDeletingDraft: boolean;
  canSaveDraft: boolean;
  onFormatChange: any;
  onChange: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => void;
  onSaveDraft: () => void;
  onDeleteDraft: () => void;
  onClose: () => void;
  onSuccess: () => void;
};

const DefaultTabContent = ({
  agent,
  draft,
  formData,
  setFormData,
  isOgrnipOverflow,
  setIsOgrnipOverflow,
  isCreateMode,
  isDraftMode,
  setIsTechnical,
  techOnly,
  provider,
  motivations,
  motivationsLoading,
  loadingTn,
  tnError,
  tnClient,
  isFirstNameAmInvalid,
  isLastNameAmInvalid,
  isPatronymicAmInvalid,
  isFormValid,
  isDirty,
  isSavingDraft,
  isDeletingDraft,
  canSaveDraft,
  onFormatChange,
  onChange,
  onSaveDraft,
  onDeleteDraft,
  onClose,
  onSuccess,
}: DefaultTabContentProps) => {
  const { t } = useTranslation();

  return (
    <>
      <BaseDataSection
        formData={formData}
        isCreate={isCreateMode || isDraftMode}
        onFormatChange={onFormatChange}
        onTechnicalChange={(checked) => {
          setIsTechnical(checked);
          setFormData((prev) => ({ ...prev, is_technical: checked }));
        }}
      />

      {formData.format && (
        <>
          {!techOnly && (
            <ContractSection
              provider={provider}
              formData={formData}
              setFormData={setFormData}
              onChange={onChange}
            />
          )}

          <Section>
            {!techOnly && (
              <MotivationSection
                formData={formData}
                provider={provider}
                motivations={motivations}
                motivationsLoading={motivationsLoading}
                setFormData={setFormData}
              />
            )}

            <NameSection
              formData={formData}
              isCreate={isCreateMode || isDraftMode}
              provider={provider}
              techOnly={techOnly}
              loadingTn={loadingTn}
              tnError={tnError}
              tnClient={tnClient}
              isFirstNameAmInvalid={isFirstNameAmInvalid}
              isLastNameAmInvalid={isLastNameAmInvalid}
              isPatronymicAmInvalid={isPatronymicAmInvalid}
              onChange={onChange}
              setFormData={setFormData}
            />

            {!techOnly && provider === Provider.BELARUS && (
              <BelarusCitizensSection
                formData={formData}
                isCreate={isCreateMode || isDraftMode}
                onChange={onChange}
              />
            )}
          </Section>
        </>
      )}

      {!formData.format && (
        <Alert severity="info">
          {t("createAgent.defaultTab.selectLegalForm")}
        </Alert>
      )}

      {formData.format && (
        <>
          {!techOnly && (
            <>
              {(formData.format === AgentLegalForm.PERSON ||
                formData.format === AgentLegalForm.ENTREPRENEUR) && (
                  <PassportSection
                    formData={formData as any}
                    setFormData={setFormData}
                    onChange={onChange}
                  />
                )}

              {formData.format === AgentLegalForm.ENTREPRENEUR && (
                <EntrepreneurSection
                  formData={formData as any}
                  provider={provider}
                  isOgrnipOverflow={isOgrnipOverflow}
                  setIsOgrnipOverflow={setIsOgrnipOverflow}
                  setFormData={setFormData}
                  onChange={onChange}
                />
              )}

              {formData.format === AgentLegalForm.COMPANY && (
                <CompanySection
                  formData={formData as any}
                  provider={provider}
                  isOgrnipOverflow={isOgrnipOverflow}
                  setIsOgrnipOverflow={setIsOgrnipOverflow}
                  setFormData={setFormData}
                  onChange={onChange}
                />
              )}

              <DocumentsSection
                formData={formData}
                setFormData={setFormData}
                provider={provider}
                onChange={onChange}
              />

              {formData.format === AgentLegalForm.COMPANY && (
                <ContactPersonSection
                  formData={formData}
                  setFormData={setFormData}
                  onChange={onChange}
                />
              )}

              <AddressesSection
                formData={formData}
                provider={provider}
                onChange={onChange}
              />
            </>
          )}

          <ContactsSection
            formData={formData}
            techOnly={techOnly}
            setFormData={setFormData}
            onChange={onChange}
          />
        </>
      )}

      {!isCreateMode && !isDraftMode && (
        <CloseAgentSection
          agent={agent}
          formData={formData}
          setFormData={setFormData}
          onSuccess={onSuccess}
        />
      )}

      <FormActions
        isCreateMode={isCreateMode}
        isDraftMode={isDraftMode}
        isFormValid={isFormValid}
        isDirty={isDirty}
        isSavingDraft={isSavingDraft}
        isDeletingDraft={isDeletingDraft}
        canSaveDraft={canSaveDraft}
        onSaveDraft={onSaveDraft}
        onDeleteDraft={onDeleteDraft}
        onClose={onClose}
      />
    </>
  );
};

export default DefaultTabContent;
